// ReservationsPage.jsx
import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Stack,
    Typography,
    Paper,
    TextField,
    MenuItem,
    Chip,
    Checkbox,
    FormControlLabel,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    TableContainer,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import SmokingRoomsIcon from "@mui/icons-material/SmokingRooms";
import SmokingRoomsOutlinedIcon from "@mui/icons-material/SmokingRoomsOutlined";
import {getAllReservations} from "../api/AiAgentApi";
import {useNavigate} from "react-router-dom";

const ZONES = [
    {value: "", label: "Все зоны"},
    {value: "Lounge", label: "Lounge"},
    {value: "Coworking", label: "Coworking"},
    {value: "Vip", label: "VIP"},
];

const STATUS_AMO_CRM = [
    {value: "", label: "Все"},
    {value: "Без подтверждения"},
    {value: "Неразобранное"},
    {value: "Новая заявка"},
    {value: "В обработке"},
    {value: "Лист ожидания"},
    {value: "Забронировано"},
    {value: "Завтра"},
    {value: "Сегодня"},
    {value: "Пришел - Успешно реализовано"},
    {value: "Закрыто и не реализовано"},
    {value: "Undefined"}
]

function formatTs(ts) {
    try {
        return new Date(ts).toLocaleString("ru-RU");
    } catch {
        return ts ?? "";
    }
}

// сортировка
function descendingComparator(a, b, orderBy) {
    const av = a?.[orderBy];
    const bv = b?.[orderBy];
    if (bv == null && av != null) return -1;
    if (av == null && bv != null) return 1;

    if (orderBy === "timestamp") {
        const at = av ? new Date(av).getTime() : 0;
        const bt = bv ? new Date(bv).getTime() : 0;
        if (bt < at) return -1;
        if (bt > at) return 1;
        return 0;
    }
    if (typeof bv === "number" && typeof av === "number") {
        return bv - av;
    }
    const as = String(av ?? "").toLowerCase();
    const bs = String(bv ?? "").toLowerCase();
    if (bs < as) return -1;
    if (bs > as) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => {
            const order = comparator(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        })
        .map((el) => el[0]);
}

export default function ReservationsPage() {
    const navigate = useNavigate();

    const [rowsRaw, setRowsRaw] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState("");

    // фильтры
    const [q, setQ] = useState("");
    const [zone, setZone] = useState("");
    const [amoCrmStatus, setAmoCrmStatus] = useState("");
    const [hookahOnly, setHookahOnly] = useState(false);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    // сортировка
    const [order, setOrder] = useState("desc");
    const [orderBy, setOrderBy] = useState("id");

    const load = async () => {
        setLoading(true);
        setErrorText("");
        try {
            const data = await getAllReservations();
            setRowsRaw(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            setErrorText("Не удалось загрузить список броней");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const filtered = useMemo(() => {
        const qLower = q.trim().toLowerCase();
        const fromMs = dateFrom ? new Date(dateFrom).getTime() : null;
        const toMs = dateTo ? new Date(dateTo).getTime() : null;

        return rowsRaw.filter((r) => {
            if (zone && r.zone !== zone) return false;
            if (amoCrmStatus && r.amoCrmStatusName !== amoCrmStatus) return false;
            if (hookahOnly && !r.hookah) return false;

            if (fromMs || toMs) {
                const t = r.timestamp ? new Date(r.timestamp).getTime() : null;
                if (fromMs && (!t || t < fromMs)) return false;
                if (toMs && (!t || t > toMs)) return false;
            }

            if (qLower) {
                const haystack = [
                    r.name,
                    r.phone,
                    r.comment,
                    r.zone,
                    r.amoCrmStatusName,
                    String(r.amoCrmId ?? ""),
                    String(r.amoCrmStatus ?? ""),
                    r.user?.id ? `u#${r.user.id}` : "",
                    r.user?.telegramId ? `tg:${r.user.telegramId}` : "",
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(qLower)) return false;
            }
            return true;
        });
    }, [amoCrmStatus, rowsRaw, q, zone, hookahOnly, dateFrom, dateTo]);

    const sorted = useMemo(
        () => stableSort(filtered, getComparator(order, orderBy)),
        [filtered, order, orderBy]
    );

    const paged = useMemo(() => {
        return sorted
    }, [sorted]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    return (
        <Box sx={{p: 3}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="h5" sx={{mb: 2}}>
                    Брони
                </Typography>
                <IconButton onClick={load} disabled={loading}>
                    <RefreshIcon/>
                </IconButton>
            </Stack>

            <Paper sx={{p: 2, mb: 2}}>
                <Stack
                    direction={{xs: "column", sm: "row"}}
                    spacing={2}
                    alignItems={{xs: "stretch", sm: "center"}}
                >
                    <TextField
                        label="Поиск (имя, телефон, статус, комментарий)"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        select
                        label="Зона"
                        value={zone}
                        onChange={(e) => setZone(e.target.value)}
                        sx={{minWidth: 180}}
                    >
                        {ZONES.map((z) => (
                            <MenuItem key={z.value} value={z.value}>
                                {z.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={hookahOnly}
                                onChange={(e) => setHookahOnly(e.target.checked)}
                            />
                        }
                        label="Только с кальяном"
                    />
                </Stack>

                <Divider sx={{my: 2}}/>

                <Stack
                    direction={{xs: "column", sm: "row"}}
                    spacing={2}
                    alignItems={{xs: "stretch", sm: "center"}}
                >
                    <TextField
                        label="С даты/времени"
                        type="datetime-local"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        InputLabelProps={{shrink: true}}
                        sx={{minWidth: 230}}
                    />
                    <TextField
                        label="По дату/время"
                        type="datetime-local"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        InputLabelProps={{shrink: true}}
                        sx={{minWidth: 230}}
                    />

                    <TextField
                        select
                        label="Статус AmoCrm"
                        value={amoCrmStatus}
                        onChange={(e) => setAmoCrmStatus(e.target.value)}
                        sx={{minWidth: 180}}
                    >
                        {STATUS_AMO_CRM.map((z) => (
                            <MenuItem key={z.value} value={z.value}>
                                {z.label ? z.label : z.value}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </Paper>

            <Paper>
                <TableContainer sx={{maxHeight: 640}}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                {[
                                    {key: "id", label: "ID", width: 80},
                                    {key: "timestamp", label: "Дата/время", width: 170},
                                    {key: "name", label: "Имя", width: 160},
                                    {key: "phone", label: "Телефон", width: 150},
                                    {key: "zone", label: "Зона", width: 120},
                                    {key: "persons", label: "Гостей", width: 90},
                                    {key: "hookah", label: "Кальян", width: 110, disableSort: true},
                                    {key: "comment", label: "Комментарий", width: 260, disableSort: true},
                                    {key: "amoCrmStatusName", label: "Статус (amoCRM)", width: 180},
                                    {key: "amoCrmId", label: "Лид ID", width: 110},
                                    {key: "user", label: "Пользователь", width: 180, disableSort: true},
                                ].map((col) => (
                                    <TableCell key={col.key} sx={{width: col.width}}>
                                        {col.disableSort ? (
                                            col.label
                                        ) : (
                                            <TableSortLabel
                                                active={orderBy === col.key}
                                                direction={orderBy === col.key ? order : "asc"}
                                                onClick={() => handleRequestSort(col.key)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paged.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{py: 4}}>
                                        {errorText || (loading ? "Загрузка..." : "Нет данных")}
                                    </TableCell>
                                </TableRow>
                            )}

                            {paged.map((r) => (
                                <TableRow
                                    hover
                                    key={r.id ?? `${r.phone}-${r.timestamp}`}
                                    sx={{"&:last-child td, &:last-child th": {border: 0}, cursor: "pointer"}}
                                    onClick={(e) => {
                                        navigate(`/messages?reservationId=${r.id}`)
                                    }}
                                >
                                    <TableCell>{r.id ?? "—"}</TableCell>
                                    <TableCell>{r.timestamp ? formatTs(r.timestamp) : "—"}</TableCell>
                                    <TableCell>{r.name ?? "—"}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <PhoneIphoneIcon fontSize="small" sx={{opacity: 0.7}}/>
                                            <span>{r.phone ?? "—"}</span>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        {r.zone ? <Chip size="small" label={r.zone}/> : "—"}
                                    </TableCell>
                                    <TableCell>{r.persons ?? "—"}</TableCell>
                                    <TableCell>
                                        {r.hookah ? (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <SmokingRoomsIcon fontSize="small"/>
                                                <span>Да</span>
                                            </Stack>
                                        ) : (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <SmokingRoomsOutlinedIcon fontSize="small"/>
                                                <span>Нет</span>
                                            </Stack>
                                        )}
                                    </TableCell>
                                    <TableCell sx={{maxWidth: 380}} title={r.comment ?? ""}>
                    <span
                        style={{
                            display: "inline-block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: 360,
                            verticalAlign: "bottom",
                        }}
                    >
                      {r.comment ?? "—"}
                    </span>
                                    </TableCell>
                                    <TableCell>
                                        {r.amoCrmStatusName ? (
                                            <Chip size="small" variant="outlined" label={r.amoCrmStatusName}/>
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                    <TableCell>{r.amoCrmId ?? "—"}</TableCell>
                                    <TableCell>
                                        {r.user ? (
                                            <Tooltip
                                                title={`ID: ${r.user.id ?? "—"} • Telegram: ${r.user.telegramId ?? "—"}`}
                                                onClick={(e) => {
                                                    navigate(`/users/${r.user.id}/analyzer`);
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`u#${r.user.id ?? "—"} / tg:${r.user.telegramId ?? "—"}`}
                                                />
                                            </Tooltip>
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
}
